/* eslint-disable no-irregular-whitespace */
import {
  Container,
  Flex,
  Heading,
  useColorModeValue,
  Box,
  Text,
  VStack,
  Icon,
  Divider,
} from "@chakra-ui/react";
import ChakraUIRenderer from "chakra-ui-markdown-renderer";
import type { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { FiFileText } from "react-icons/fi";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import style from "../lib/styles/markdown.module.css";
import Banner from "lib/components/home/Banner";

// Hoisted to module scope — ChakraUIRenderer() is expensive to call on every render
const chakraMarkdownComponents = ChakraUIRenderer();

interface TermsData {
  date_created: string;
  date_updated: string;
  content: string;
}

interface TermsProps {
  termsData: TermsData;
  lastUpdated: string;
}

export const getStaticProps: GetStaticProps<TermsProps> = async () => {
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_DIRECTUS_URL}/items/terms_and_conditions`
  );
  const data = await res.json();
  const terms = data.data;

  const lastUpdated = terms.date_updated || terms.date_created;

  return {
    props: {
      termsData: terms,
      lastUpdated,
    },
    revalidate: 60,
  };
};

const Terms = ({ termsData, lastUpdated }: TermsProps) => {
  const updated = new Date(lastUpdated);

  const bgGradient = useColorModeValue(
    "linear(to-b, neutral.50, white)",
    "linear(to-b, neutral.900, neutral.800)"
  );
  const iconColor = useColorModeValue("brand.500", "brand.400");
  const subtitleColor = useColorModeValue("neutral.600", "neutral.400");
  const contentBg = useColorModeValue("white", "neutral.800");
  const contentBorder = useColorModeValue("neutral.100", "neutral.700");
  const dateBg = useColorModeValue("neutral.50", "neutral.700");

  return (
    <>
      <NextSeo
        title="Terms & Conditions"
        description="Adiona Alert Terms & Conditions"
      />
      <Banner />

      {/* Hero Section */}
      <Box
        bgGradient={bgGradient}
        pt={{ base: 12, md: 20 }}
        pb={{ base: 8, md: 14 }}
      >
        <Container maxW="4xl">
          <VStack spacing={5} textAlign="center">
            <Icon as={FiFileText} boxSize={12} color={iconColor} />
            <Heading
              fontWeight={700}
              fontSize={{ base: "3xl", sm: "4xl", md: "5xl" }}
              letterSpacing="tight"
              lineHeight="shorter"
              as="h1"
            >
              Terms & Conditions
            </Heading>
            <Box bg={dateBg} px={4} py={2} borderRadius="full" mt={2}>
              <Text fontSize="sm" color={subtitleColor} fontWeight={500}>
                Last updated:{" "}
                {updated.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Text>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Content */}
      <Container maxW="4xl" py={{ base: 10, md: 16 }} px={{ base: 4, md: 8 }}>
        <Box
          bg={contentBg}
          border="1px solid"
          borderColor={contentBorder}
          borderRadius="xl"
          p={{ base: 6, md: 10 }}
          shadow="sm"
        >
          <Flex
            align="center"
            justify="flex-start"
            wrap="wrap"
            direction="column"
          >
            <ReactMarkdown
              components={chakraMarkdownComponents}
              className={style.reactMarkDown}
              remarkPlugins={[remarkGfm]}
              skipHtml
            >
              {termsData.content}
            </ReactMarkdown>
          </Flex>

          <Divider my={8} />

          <Text fontSize="sm" color={subtitleColor} textAlign="center">
            If you have any questions about these terms, please contact us.
          </Text>
        </Box>
      </Container>
    </>
  );
};

export default Terms;
